/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import produce from 'immer';
import _ from 'lodash';
import {
  SaveSheet,
  SheetStatus,
  SheetToSave,
} from 'src/common/types/SaveSheet';
import { w3cwebsocket as W3CWebSocket } from 'websocket';

import {
  useLazyFileUploadRequestQuery,
  useUploadFileMutation,
  useSaveMultipleNotesMutation,
} from '../../common/apis/FlorenceApi';
import { useAppSelector } from '../../common/hooks/reduxHooks';
import { useAppDispatch } from '../../common/hooks/reduxHooks';
import { clearProcessedOcrs, updateOcr } from '../../common/stores/modelSlice';
import { OcrResult } from '../../common/types/OcrResult';
import SnackbarUtils from '../../common/utils/SnackbarUtilsConfigurator';
import { ProcessedOcr } from '../types/ProcessedOcr';

export const useFileUpload = () => {
  const dispatch = useAppDispatch();
  const ws = React.useRef<W3CWebSocket | null>(null);
  const { user, processedOcrs } = useAppSelector((state) => state.model);
  const [uploadRequestTrigger] = useLazyFileUploadRequestQuery();
  const [uploadFileTrigger] = useUploadFileMutation();
  const [saveMultipleNotes, { isLoading: isSavingSheets }] =
    useSaveMultipleNotesMutation();

  const [processedResults, setProcessedResults] = React.useState<OcrResult[]>(
    []
  );
  const [isClinicNote, setIsClinicNote] = React.useState(false);
  const [savedNotes, setSavedNotes] = React.useState<SaveSheet[]>([]);

  const getOcrByResult = (result: OcrResult) => {
    const getUUID = (urlString: string) => {
      if (!urlString) return '';
      const url = new URL(urlString);
      return url.pathname.split('/')[2].split('.')[0];
    };

    const index = processedOcrs.findIndex(
      (o) => getUUID(o.uploadUrl) === result.documentName.split('.')[0]
    );

    if (index !== -1) {
      return processedOcrs[index];
    }

    return null;
  };

  React.useEffect(() => {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return;
    if (ws.current && (ws.current.CONNECTING || ws.current.OPEN)) return;

    // SECURITY: the Cognito idToken is passed as a query-string parameter on the
    // WebSocket URL. That JWT is PHI-scoped (it authorizes OCR of clinical
    // documents), and full request URLs are routinely persisted in places we do
    // not control or scrub: API Gateway / CloudWatch access logs, ALB + CloudFront
    // logs, any proxy in front of the client, browser history, and Referer-style
    // telemetry. A token that leaks from any of those is replayable until it
    // expires. Keeping it here for now because the API Gateway WebSocket
    // authorizer reads the `token` query parameter on $connect and changing the
    // client alone would break all uploads.
    // TODO(security): move the credential off the URL. Two workable options:
    //   1. Connect unauthenticated and send the idToken in the first message
    //      frame, having $connect defer authorization to that frame; or
    //   2. Mint a short-lived (<=60s), single-use connect ticket from the REST
    //      API and put the ticket -- not the idToken -- in the query string.
    // Either change must land in the authorizer and this client together.
    //
    // NOTE: the trailing `/` after ${idToken} is almost certainly a typo -- it
    // makes the `token` query value `<jwt>/`, which is not a well-formed JWT.
    // It is left in place deliberately: the authorizer that consumes it is
    // deployed outside this repo, so we cannot confirm it is not compensating
    // for the extra character, and uploads currently work with it. Remove it
    // only alongside a check of the $connect authorizer.
    ws.current = new W3CWebSocket(
      `${process.env.REACT_APP_API_WS_URL}?token=${idToken}/`
    );

    ws.current.onerror = () => {
      SnackbarUtils.error(
        'Error with the websocket uploading connection. Please try again later.'
      );
    };

    ws.current.onmessage = (message) => {
      const result = JSON.parse(message.data as string) as OcrResult;
      result.result = trimScannedText(result.result);
      setProcessedResults((prev) => [...(prev ?? []), result]);
    };
    const wsCurrent = ws.current;

    // reset OCRs on page load because they are cached by the global state
    dispatch(clearProcessedOcrs());

    return () => {
      if (wsCurrent.readyState === wsCurrent.OPEN) {
        try {
          wsCurrent.close();
        } catch (e) {
          console.error(e);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const updateOcrByResult = (result: OcrResult) => {
      const ocr = getOcrByResult(result);
      if (ocr) {
        const updated = produce(ocr, (draft) => {
          draft.isUploading = false;
          draft.uploadComplete = true;
          draft.ocrResult = result;
        });
        dispatch(updateOcr(updated));
      }
    };

    const lastProcessedResult = processedResults[processedResults.length - 1];
    if (lastProcessedResult) {
      updateOcrByResult(lastProcessedResult);
    }

    // fix the bug where some OCRs are not updated because the processedResults is updated too fast therefore some "lastProcessedResult" are not triggering this useEffect
    // update all of them here since all the results have been processed
    // the above code with the lastProcessedResult is only for the UI, to show the user that some OCRs are uploaded faster
    if (
      processedResults.length > 0 &&
      processedResults.length === processedOcrs.length
    ) {
      for (const result of processedResults) {
        updateOcrByResult(result);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedResults]);

  React.useEffect(() => {
    // if all the results have been received, save the sheets

    const uploadedOcrs = processedOcrs.filter(
      (o) => o.uploadComplete && o.savedSheetId === -1
    );

    if (
      !isSavingSheets &&
      uploadedOcrs.length > 0 &&
      uploadedOcrs.length === processedOcrs.length
    ) {
      saveTheNotes().then((sheets) => {
        if (!sheets) {
          for (const ocr of processedOcrs) {
            const updated = produce(ocr, (draft) => {
              draft.error = 'There has been an error saving your note.';
            });
            dispatch(updateOcr(updated));
          }
          return;
        }

        // the sheets and the processedOcrs must have the same index thanks to the auto incremental id of the sheets
        for (const [index, sheet] of sheets.entries()) {
          const updated = produce(processedOcrs[index], (draft) => {
            draft.savedSheetId = sheet.sheetId;
            draft.clinicNote = sheet.clinicNote;
          });
          dispatch(updateOcr(updated));
        }

        setSavedNotes([...savedNotes, ...sheets]);
        setProcessedResults([]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedOcrs]);

  const requestUploadUrl = React.useCallback(
    async (ocr: ProcessedOcr) => {
      const { file } = ocr.wrappedFile;
      if (!user) throw new Error('User does not exist');
      try {
        const { data } = await uploadRequestTrigger({
          contentType: file.type,
          fileExtension: file.name.split('.').pop() ?? '',
          originalFileName: file.name,
          userId: user.id,
        });
        if (!data) {
          console.error('Unable to get upload url');
          return;
        }
        const updated = produce(ocr, (draft) => {
          draft.uploadUrl = data;
        });
        dispatch(updateOcr(updated));
        return data;
      } catch (error) {
        throw error;
      }
    },
    [dispatch, uploadRequestTrigger, user]
  );

  const readFile = React.useCallback(async (ocr: ProcessedOcr) => {
    const { file } = ocr.wrappedFile;
    return new Promise<ArrayBuffer | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onabort = () => reject(new Error('File read aborted'));
      reader.onerror = () => reject(new Error('File read error'));
      reader.onload = () => resolve(reader.result as ArrayBuffer | null);
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const uploadFile = React.useCallback(
    async (ocr: ProcessedOcr) => {
      const updated = produce(ocr, (draft) => {
        draft.isUploading = true;
      });
      dispatch(updateOcr(updated));
      try {
        const data = await readFile(updated);
        const url = await requestUploadUrl(updated);
        if (!data || !url) {
          console.error('Invalid data or file url');
          return;
        }
        await uploadFileTrigger({
          body: data,
          url,
          contentType: updated.wrappedFile.type,
        });
      } catch (error: any) {
        if (error.message) {
          SnackbarUtils.error(error.message);
          const updated = produce(ocr, (draft) => {
            draft.isUploading = false;
            draft.error = error.message;
            draft.uploadComplete = true;
          });
          dispatch(updateOcr(updated));
          return;
        }
        SnackbarUtils.error('Unexpected error');
        const updated = produce(ocr, (draft) => {
          draft.isUploading = false;
          draft.error = error.message;
          draft.uploadComplete = true;
        });
        dispatch(updateOcr(updated));
      }
    },
    [dispatch, readFile, requestUploadUrl, uploadFileTrigger]
  );

  const beginUpload = React.useCallback(
    (clinicNote: boolean) => {
      setIsClinicNote(clinicNote);
      const uploadableOcrs = processedOcrs.filter(
        (processedOcrs) =>
          !(processedOcrs.isUploading || processedOcrs.uploadComplete)
      );
      if (!_.isEmpty(uploadableOcrs)) {
        for (const ocr of processedOcrs) {
          uploadFile(ocr);
        }
      }
    },
    [processedOcrs, uploadFile]
  );

  const trimScannedText = (text: string) => {
    // trim and remove first and last chars if they are " and trim again
    text = text.trim();
    const firstChar = text.charAt(0);
    const lastChar = text.charAt(text.length - 1);
    if (firstChar === `"` || firstChar === `'`) text = text.slice(1);
    if (lastChar === `"` || lastChar === `'`) text = text.slice(0, -1);

    return text.trim();
  };

  const saveTheNotes = async () => {
    const notesToSave: SheetToSave[] = [];

    for (const ocr of processedOcrs) {
      if (!ocr.ocrResult) continue;

      const { result: text, filePath, documentName } = ocr.ocrResult;
      notesToSave.push({
        scanText: text,
        scanDocumentUrl: filePath,
        sheetCodes: [],
        name: ocr.fileName,
        documentName: documentName,
        status: SheetStatus.UPLOADED,
        clinicNote: isClinicNote,
      });
    }

    try {
      return await saveMultipleNotes(notesToSave).unwrap();
    } catch {
      SnackbarUtils.error(
        'There has been an error saving your notes. Please try again later.'
      );
      return null;
    }
  };

  const clearAll = () => {
    dispatch(clearProcessedOcrs());
    setProcessedResults([]);
  };

  return {
    ocrs: processedOcrs,
    beginUpload,
    uploading: isSavingSheets || !ws.current,
    clearAll,
    savedNotes,
  };
};
